'use client'

import {
  Col,
  Row,
  Typography,
  Separator,
  AspectRatio,
  NumberUnit,
  Button,
  Icon,
  DeltaBadge,
  Timer,
  Alert,
} from '@repo/ui/components'
import { useLanguage, useTranslate } from '@repo/shared/hooks'
import Image from 'next/image'
import {
  AgentPost,
  AgentProject,
  useGetAgentReferralCodes,
} from '@repo/services'
import { ButtonLink, ValueWithUnit, PaymentType } from '@repo/shared/components'
import { globalConfig } from '@repo/shared/utils/global-config'
import { useIsDesktop } from '@repo/ui/hooks'
import { cn } from '@repo/ui/lib'
import { useState } from 'react'
import { PropertySelectionModal } from './dashboard.property-selection-modal'

interface PropertyCardProps {
  property: AgentProject
  post?: AgentPost
  className?: string
  layout: 'dashboard' | 'projects' | 'add-project'
}

const ImageComp = ({
  src,
  layout,
}: {
  src?: string
  layout?: 'dashboard' | 'projects' | 'add-project'
}) => {
  return (
    <div
      className={cn(
        layout === 'dashboard'
          ? 'w-[72px] h-[48px] md:w-[100%]   md:h-[100%] rounded-xs'
          : 'w-[100%] h-[100%] md:rounded-xs',
        '',
      )}
    >
      <AspectRatio ratio={16 / 9}>
        <Image
          src={src || '/images/placeholder.webp'}
          alt='Photo of property'
          fill
          className='h-full w-full   rounded-2xs object-cover'
          loading='lazy'
        />
      </AspectRatio>
    </div>
  )
}

export const PropertyCard = ({
  property,
  post,
  className,
  layout,
}: PropertyCardProps) => {
  const { t } = useTranslate()
  const language = useLanguage()
  const isDesktop = useIsDesktop()
  const { data } = useGetAgentReferralCodes()
  const referralCode = data?.data?.codes?.[0]?.code
  const [typeOfModal, setTypeOfModal] = useState<
    'choose' | 'details' | undefined
  >(undefined)

  const getLogoUrl = (url?: string) =>
    url ? `${globalConfig.baseImageUrl}${url}` : '/images/placeholder.webp'

  const galleryImages =
    property.photos
      ?.map((photo) => getLogoUrl(photo.bigURL))
      .filter((url): url is string => Boolean(url)) || []

  const soldByYou = 0

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        text: t('dashboardAgent_projects_share_text', {
          name: property.displayName || '',
          url: `${globalConfig.originWebUrl}/rwa/${property.token}/aff/${referralCode}`,
        }),
        // url: `${globalConfig.originWebUrl}/projects/${property.token}`,
      })
    }
  }

  return (
    <Row
      className={cn(
        'flex-col md:flex-row gap-md px-xl md:px-md py-md justify-between overflow-hidden ',
        className,
      )}
    >
      {!isDesktop && layout !== 'dashboard' && (
        <ImageComp src={galleryImages[0]} layout={layout} />
      )}
      <Col className='gap-md'>
        {/* Property Info */}
        <Row className='gap-md items-center'>
          <Col className='gap-xs'>
            <Typography
              size='md'
              weight='bold'
              color='text-neutral-surface-100'
            >
              {property.displayName}
            </Typography>

            <Row className='gap-md'>
              <Typography size='xs' color='text-neutral-surface-60'>
                {property.location?.province?.name}
              </Typography>
              <Typography size='xs' color='text-neutral-surface-60'>
                {property.location?.district?.name}
              </Typography>
              <Typography size='xs' color='text-neutral-surface-60'>
                {t(`common_${property.type}`)}
              </Typography>
            </Row>
          </Col>
          {!isDesktop && layout === 'dashboard' && (
            <ImageComp src={galleryImages[0]} layout={layout} />
          )}
        </Row>
        <Separator />
        {(post?.status === 'draft' || post?.status === 'in_review') && (
          <Alert
            size='md'
            color='alert'
            caption={t(
              'dashboardAgent_your_opinion_about_property_is_under_review',
            )}
            icon={<Icon name='AlertTriangle' size='sm' />}
          />
        )}

        {layout === 'projects' && !post && (
          <Alert
            caption={t('dashboardAgent_add_expert_opinion_add_alert')}
            color='alert'
          />
        )}
        {post && post.status === 'rejected' && (
          <Alert
            caption={t('dashboardAgent_add_expert_opinion_rejected_alert', {
              reason: post.audit?.comments?.[0] || '',
            })}
            color='negative'
          />
        )}
        {(layout === 'projects' || layout === 'add-project') && (
          <Row className='justify-between '>
            <Typography size='xs' color='text-neutral-surface-60'>
              {t('dashboardAgent_price_per_cm2')}
            </Typography>

            <PaymentType
              value={property.price?.priceRange24h?.last}
              size='2xs'
              numberSize='sm'
              isBold={true}
            />
          </Row>
        )}
        {(layout === 'projects' || layout === 'add-project') && (
          <Row className='justify-between '>
            <Typography size='xs' color='text-neutral-surface-60'>
              {t('dashboardAgent_price_per_cm2_change', {
                timePeriod: t('dashboardAgent_7days'),
              })}
            </Typography>

            <DeltaBadge
              value={property.price?.priceRange24h?.deltaPercent || 0}
              variant='text'
              size='md'
            />
          </Row>
        )}
        {layout === 'add-project' && (
          <Row className='justify-between '>
            <Typography size='xs' color='text-neutral-surface-60'>
              {t('dashboardAgent_selection_deadline')}
            </Typography>
            <Typography size='xs' color='text-neutral-surface-60'>
              <Timer
                targetDate={new Date(Date.now() + 10 * 24 * 60 * 60 * 1000)}
                onComplete={() => {}}
                language={language === 'fa' ? 'fa' : 'en'}
              />
            </Typography>
          </Row>
        )}
        {/* Area/Price Info */}
        {(layout === 'projects' || layout === 'dashboard') && (
          <Row className='justify-between '>
            <Typography size='xs' color='text-neutral-surface-60'>
              {t('dashboardAgent_your_sale_area_total')}
            </Typography>
            <Row className='gap-2xs w-fit'>
              <ValueWithUnit
                value={soldByYou}
                unit='property'
                unitSize='2xs'
                valueSize='sm'
                valueWeight='bold'
              />
              <Typography size='xs' color='text-neutral-surface-60'>
                {t('common_from')}
              </Typography>
              <ValueWithUnit
                value={(property.market?.totalSupply || 0) / 10000}
                unit='property'
                unitSize='2xs'
                valueSize='sm'
                valueWeight='regular'
              />
            </Row>
          </Row>
        )}
        {/* Statistics */}
        {(layout === 'projects' || layout === 'dashboard') && (
          <Row className='justify-between'>
            <Typography size='xs' color='text-neutral-surface-60'>
              {t('dashboardAgent_statistics')}
            </Typography>
            <Row className='gap-2xs w-fit'>
              <NumberUnit
                value={0}
                unit={t('dashboardAgent_visit')}
                numberSize='sm'
                size='2xs'
                isBold={true}
              />

              <Typography size='2xs' color='text-neutral-surface-80'>
                /
              </Typography>
              <NumberUnit
                value={0}
                unit={t('dashboardAgent_trade')}
                numberSize='sm'
                size='2xs'
                isBold={true}
              />
            </Row>
          </Row>
        )}
      </Col>

      <Col className='w-full md:w-fit md:min-w-[208px] items-center gap-xs'>
        {isDesktop && <ImageComp src={galleryImages[0]} layout={layout} />}
        <div
          className={cn(
            'flex md:flex-col w-full gap-xs',
            layout === 'projects' ? 'flex-row-reverse' : 'flex-col-reverse',
          )}
        >
          {layout === 'add-project' && (
            <Button
              variant='fill'
              width='full'
              size='sm'
              color='base'
              onClick={() => setTypeOfModal('choose')}
            >
              {t('dashboardAgent_select_property')}
            </Button>
          )}
          {layout === 'projects' && (
            <Button
              variant='fill'
              width='full'
              size='sm'
              color='base'
              startIcon={<Icon name='ShareAndroid' size='xs' />}
              disabled={post?.status !== 'published'}
              onClick={() => handleShare()}
            >
              {t('dashboardAgent_share')}
            </Button>
          )}
          {(layout === 'projects' || layout === 'dashboard') && (
            <Button
              variant='ghost'
              width='full'
              size='sm'
              color='base'
              onClick={() => setTypeOfModal('details')}
            >
              {t('dashboardAgent_details')}
            </Button>
          )}
          {layout === 'add-project' && (
            <ButtonLink
              variant='ghost'
              width='full'
              size='sm'
              color='base'
              className='w-full'
              buttonClassName='w-full'
              href={`${globalConfig.originWebUrl}/projects/${property.token}`}
              target='_blank'
            >
              {t('dashboardAgent_details_full')}
            </ButtonLink>
          )}
        </div>
      </Col>

      {/* Property Selection Modal */}
      {!!typeOfModal && (
        <PropertySelectionModal
          open={!!typeOfModal}
          viewType={typeOfModal || 'choose'}
          onOpenChange={() => setTypeOfModal(undefined)}
          property={property}
          post={post}
        />
      )}
    </Row>
  )
}
